import React from "react";
import { Disc3, Guitar, Heart, Sparkles, Gem, Lock } from "lucide-react";
import { THEMES } from "@/lib/themes";
import { useLang } from "@/i18n";

const ICONS = { Disc3, Guitar, Heart, Sparkles, Gem };

export const StyleSelector = ({ selected, onSelect }) => {
  const { t: tr } = useLang();
  return (
    <div className="w-full max-w-md" data-testid="style-selector">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-px flex-1 bg-cream/30" />
        <span
          className="font-bebas text-cream text-sm tracking-[0.3em] uppercase"
          style={{ textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}
        >
          {tr("landing.chooseStyle")}
        </span>
        <div className="h-px flex-1 bg-cream/30" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        {THEMES.map((t) => {
          const Icon = ICONS[t.icon] || Disc3;
          const active = selected === t.id;
          const disabled = !t.available;
          return (
            <button
              key={t.id}
              data-testid={`style-option-${t.id}`}
              disabled={disabled}
              onClick={() => !disabled && onSelect(t.id)}
              aria-pressed={active}
              className={`group relative flex items-center gap-3 rounded-2xl border-2 px-3 py-3 text-left backdrop-blur-xl transition-all duration-200 ${
                disabled ? "cursor-not-allowed opacity-55" : "cursor-pointer hover:-translate-y-0.5 active:scale-[0.97]"
              }`}
              style={{
                borderColor: active ? t.accent : "rgba(255,249,238,0.35)",
                backgroundColor: active ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.32)",
                boxShadow: active
                  ? `0 8px 22px rgba(0,0,0,0.45), 0 0 0 3px ${t.accent}55, inset 0 2px 6px rgba(255,255,255,0.35), inset 0 -4px 10px rgba(0,0,0,0.30)`
                  : "0 6px 16px rgba(0,0,0,0.35), inset 0 2px 6px rgba(255,255,255,0.30), inset 0 -4px 10px rgba(0,0,0,0.28)",
              }}
            >
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border-2"
                style={{ backgroundColor: `${t.accent}22`, borderColor: `${t.accent}` }}
              >
                <Icon size={20} strokeWidth={2.5} style={{ color: t.accent }} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-display font-black text-cream text-sm leading-tight drop-shadow-[0_1px_0_rgba(0,0,0,0.5)]">
                  {t.name}
                </span>
                {disabled ? (
                  <span className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-cream/70">
                    <Lock size={10} /> {tr("style.comingSoon")}
                  </span>
                ) : (
                  <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: active ? t.accent : "rgba(255,249,238,0.7)" }}>
                    {active ? tr("style.chosen") : tr("style.tapToChoose")}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
